import React, { useState, useRef, Ref, useEffect, useCallback } from 'react';
import { JobType, LaunchCallbacks, ServiceDetails, SiteStorageArea, Site } from './types';
import { LANGUAGE_OPTIONS } from './common/constants';
import { useAppDispatch } from '../app/hooks';

//	--------------------------------------------------------------------------
//
//	T Y P E S
//
//	--------------------------------------------------------------------------

//	--------------------------------------------------------------------------
//
//	R E D U X   S T A T E   M A N A G E M E N T
//
//	--------------------------------------------------------------------------
import { CONTEXT_CONTAINERS, ctx_setSize } from '../components/menus/context-menu-slice';

//	--------------------------------------------------------------------------
//
//	P U B L I C   F U N C T I O N S
//
//	--------------------------------------------------------------------------

// ------------------------------------------------------------
//
//	Determines the prefix to apply to all API calls.
//
// ------------------------------------------------------------

export function APIPrefix() {
  var currentUrl: string = getBaseURL();
  var addPort: boolean = false;

  // check if there is a port number at the end.
  if (nthIndexOf({ inputString: currentUrl, findString: ':', itemNumber: 2 }) >= 0) {
    currentUrl = currentUrl.slice(
      0,
      nthIndexOf({ inputString: currentUrl, findString: ':', itemNumber: 2 }),
    );
    addPort = true;
  }

  // if we're using localhost then we've probably got a port number (likely 3000), which should be replaced with port 8080.
  // if we're on one of the production servers then there is no port number included, but we need to append '/tannet-api' to reach the APIs.
  var apiPrefix: string = currentUrl;
  if (addPort === true) apiPrefix = apiPrefix + ':8080';
  else apiPrefix = apiPrefix + '/tannet-api';

  // return something.
  return apiPrefix;
} // APIPrefix

// ------------------------------------------------------------
//
//	Build a url from a given prefix, host, path and port.
//
// ------------------------------------------------------------

export function buildUrl(args: {
  prefix: string | undefined;
  host: string | undefined;
  path: string | undefined;
  port: number | undefined;
}) {
  //
  let url: string = '';

  if (args.prefix !== undefined) {
    url = url + args.prefix;
  } else {
    url = url + 'http';
  }
  if (!url.endsWith('://')) url = url + '://';
  if (args.host !== undefined) {
    url = url + args.host;
  } else {
    url = url + 'unknown-host';
  }
  if (args.port !== undefined) {
    url = url + ':' + args.port.toString();
  }
  if (args.path !== undefined) {
    url = url + args.path;
  }

  return url;
  //
} // buildUrl

// ------------------------------------------------------------
//
//	Checks if there has been any overflow in a DIV element
//	containing text.
//
// ------------------------------------------------------------

export function CheckOverflow(args: { ref: any; text: string }) {
  //
  const [sIsOverflow, setIsOverflow] = useState<boolean | undefined>(undefined);
  const [sTextLastCheck, setTextLastCheck] = useState<string | undefined>(undefined);

  React.useLayoutEffect(() => {
    //
    const { current } = args.ref;

    if (current) {
      //
      const { clientWidth, scrollWidth, clientHeight, scrollHeight } = current;
      /*console.log(
        'CheckOverflow size:',
        args.text,
        scrollHeight,
        scrollWidth,
        clientHeight,
        clientWidth,
      );
      if (args.text[0] === 'A' && args.text[1] === 'D') {
        console.log(
          'CheckOverflow size:',
          args.text,
          scrollHeight,
          scrollWidth,
          clientHeight,
          clientWidth,
        );
      }*/

      // an overflow has occurred if we need to scroll in either the X or Y direction.
      const hasOverflow = scrollHeight > clientHeight || scrollWidth > clientWidth;

      // update the state. we need to update the last-check value as well, in order that the process to shorted the
      // text can be stopped and the screen re-rendered.
      setIsOverflow(hasOverflow);
      setTextLastCheck(args.text);
      //
    }
    //
  }, [args.ref, args.text]);

  //if (args.text[ 0 ] === 'Z')
  //{
  //	console.log( "CheckOverflow:" );
  //	console.log( args.text );
  //	console.log( sTextLastCheck );
  //	console.log( sIsOverflow );
  //}

  // return something.
  return sIsOverflow && args.text === sTextLastCheck;
  //
} // CheckOverflow

// ------------------------------------------------------------
//
//	Define a hook that monitors the container, and
//	keeps the current size in state.
//
// ------------------------------------------------------------

export function ContainerHook(props: { container: CONTEXT_CONTAINERS }) {
  //
  //	--------------------------------------------------------------------------
  //
  //	R E D U X   S T A T E   H A N D L I N G
  //
  //	--------------------------------------------------------------------------

  const dispatch = useAppDispatch();

  //	------------------------------------------------------------
  //
  //	code for storing the size when the data window is resized.
  //
  //	------------------------------------------------------------

  // handle changes in size to the data window container.
  const resizeDataWindow = React.useRef<ResizeObserver>(
    new ResizeObserver((entries: ResizeObserverEntry[]) => {
      //
      // update the size of the container
      if (entries[0].contentRect.width > 0 && entries[0].contentRect.height > 0) {
        dispatch(
          ctx_setSize({
            container: props.container,
            size: {
              x: entries[0].contentRect.width,
              y: entries[0].contentRect.height,
            },
          }),
        );
      }
      //
    }),
  );

  // create a reference to the HTML Div element that contains the data items, so that we can monitor for changes in size.
  const dataWindow = React.useCallback(
    (container: HTMLDivElement | null) => {
      //
      // check that this reference has actually been assigned to an element, and switch on observing.
      if (container !== null) {
        resizeDataWindow.current.observe(container);
      }

      // When element is unmounted, ref callback is called with a null argument
      // => best time to cleanup the observer
      else if (resizeDataWindow.current) {
        resizeDataWindow.current.disconnect();
      }
      //
    },
    [resizeDataWindow.current],
  );

  // return the reference
  return dataWindow;
  //
} // ContainerHook

// ------------------------------------------------------------
//
//	Displays a file size in either bytes,
//
// ------------------------------------------------------------

export function DisplayFileSize(args: { bytes: number }) {
  var textValue: string = '';

  if (args.bytes !== null) {
    if (args.bytes < 0) textValue = '/';
    else if (args.bytes < 1024) textValue = args.bytes.toFixed(2) + ' B';
    else if (args.bytes < 1024 * 1024) textValue = (args.bytes / 1024).toFixed(2) + ' KB';
    else if (args.bytes < 1024 * 1024 * 1024)
      textValue = (args.bytes / (1024 * 1024)).toFixed(2) + ' MB';
    else if (args.bytes < 1024 * 1024 * 1024 * 1024)
      textValue = (args.bytes / (1024 * 1024 * 1024)).toFixed(2) + ' GB';
    else textValue = (args.bytes / (1024 * 1024 * 1024 * 1024)).toFixed(2) + ' TB';
  } else textValue = 'size is null';

  // return something.
  return textValue;
} // DisplayFileSize

// ------------------------------------------------------------
//
//	Extracts the namespace and name from the
//	access_url
//
// ------------------------------------------------------------

export function extractFileFromAccessUrl(args: { accessUrl: string }) {
  //
  var namespace: string = '';
  var filename: string = '';
  if (args.accessUrl.indexOf('id=') > -1) {
    //
    // get the rest of the URL.
    var remainder: string = args.accessUrl.slice(
      args.accessUrl.indexOf('id=') + 3 - args.accessUrl.length,
    );
    if (remainder.indexOf(':') > -1) {
      namespace = remainder.slice(0, remainder.indexOf(':'));
      filename = remainder.slice(remainder.indexOf(':') + 1 - remainder.length);
    } else {
      filename = remainder;
    }
    //
  }

  // return something.
  return {
    namespace: namespace,
    filename: filename,
  };
  //
} // extractFileFromAccessUrl

// ------------------------------------------------------------
//
//	Extracts the namespace and name from the
//	obs_publisher_did
//
// ------------------------------------------------------------

export function extractFileFromObsPublisherDid(args: { obsPublisherDid: string }) {
  //
  var namespace: string = '';
  var filename: string = '';
  if (args.obsPublisherDid.indexOf('?') > -1) {
    //
    // get the rest of the URL.
    var remainder: string = args.obsPublisherDid.slice(
      args.obsPublisherDid.indexOf('?') + 1 - args.obsPublisherDid.length,
    );
    if (remainder.indexOf(':') > -1) {
      namespace = remainder.slice(0, remainder.indexOf(':'));
      filename = remainder.slice(remainder.indexOf(':') + 1 - remainder.length);
    } else {
      filename = remainder;
    }
    //
  }

  // return something.
  return {
    namespace: namespace,
    filename: filename,
  };
  //
} // extractFileFromObsPublisherDid

// ------------------------------------------------------------
//
//	Extracts the namespace and name from the
//	obs_id
//
// ------------------------------------------------------------

export function extractFileFromObsID(args: { obsID: string }) {
  //
  var namespace: string = '';
  var filename: string = '';
  if (args.obsID.indexOf(':') > -1) {
    //
    namespace = args.obsID.slice(0, args.obsID.indexOf(':'));
    filename = args.obsID.slice(args.obsID.indexOf(':') + 1 - args.obsID.length);
  } else {
    filename = args.obsID;
  }

  // return something.
  return {
    namespace: namespace,
    filename: filename,
  };
  //
} // extractFileFromObsID

// ------------------------------------------------------------
//
//	Gets the current URL. i.e. https://domain:port
//
// ------------------------------------------------------------

export function getBaseURL() {
  const { protocol, hostname, port } = window.location;
  return `${protocol}//${hostname}${port ? `:${port}` : ''}`;
} // getBaseURL

// ------------------------------------------------------------
//
//	Monitors for mouse clicks so that a dropdown menu
//	attached to {ref} can be closed.
//
// ------------------------------------------------------------

export function HideDropdownMenu(args: {
  sDropdownDisplayed: boolean | undefined;
  setDropdownDisplayed: any;
  redux?: boolean;
}) {
  //
  //	--------------------------------------------------------------------------
  //
  //	R E D U X   S T A T E   H A N D L I N G
  //
  //	--------------------------------------------------------------------------

  const dispatch = useAppDispatch();

  const ref = useRef<HTMLDivElement>(null);

  // ------------------------------------------------------------
  //
  //	handle click events, and check if we've clicked on
  //	the dropdown menu, or the previous cell, which should
  //	be the dropdown button.
  //
  // ------------------------------------------------------------

  const handleClickOutside = (event: any) => {
    var currentCell: boolean = false;
    var previousCell: boolean = false;

    if (ref.current) {
      currentCell = ref.current.contains(event.target);
      if (ref.current.previousSibling)
        previousCell = ref.current.previousSibling.contains(event.target);
    }
    if (currentCell === false && previousCell === false) {
      if (args.redux === true) {
        dispatch(args.setDropdownDisplayed(false));
      } else {
        args.setDropdownDisplayed(false);
      }
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside, true);
    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, []);

  return { ref };
  //
} // HideDropdownMenu

// ------------------------------------------------------------
//
//	Reduce a text string by one character.
//	We add the dots (...) to the section where the
//	character was removed.
//	We don't take off numbers on the right-hand side.
//
// ------------------------------------------------------------

export function ShortenText(args: {
  text: string;
  setText: any;
  setTextWithoutDots: any;
  retainTrailingNumbers?: boolean;
}) {
  //
  //console.log('CheckOverflow size: (shorten text):', args.text);

  // shrink the text if there is an overflow.
  var text: string = args.text;
  const len: number = text.length;

  // look for the first non-numeric character from the right.
  var foundChar: boolean = false;
  var newText: string = '';
  var newTextWithoutDots: string = '';

  if (args.retainTrailingNumbers !== true) {
    foundChar = true;
    newText = text.substring(0, len - 1) + '...';
    newTextWithoutDots = text.substring(0, len - 1);
  } else {
    //
    if (len > 0) {
      for (var i = len - 1; i >= 0; i--) {
        var alpha: boolean = !(text[i] >= '0' && text[i] <= '9');
        if (alpha === false || foundChar === true) {
          newText = text[i] + newText;
          newTextWithoutDots = text[i] + newTextWithoutDots;
        }
        if (alpha === true && foundChar === false) {
          newText = '...' + newText;
          foundChar = true;
        }
      }
    }
  }
  if (newText !== text && newTextWithoutDots !== '') {
    args.setText(newText);
    args.setTextWithoutDots(newTextWithoutDots);
  }
  //
} // ShortenText

//	-------------------------------------------------
//
//	Removes double quotes from around a string.
//
//	-------------------------------------------------

export function StripDoubleQuotes(args: { value: string | null }) {
  var outValue: string = '';
  if (args.value !== null)
    if (args.value[0] === '\"' && args.value[args.value.length - 1] === '\"')
      outValue = args.value.slice(1, args.value.length - 1);

  // return something.
  return outValue;
} // StripDoubleQuotes

//	-------------------------------------------------
//
//	Function to check Session Validity
//
//	-------------------------------------------------

export async function hasSessionExpired() {
  const response = await fetch(APIPrefix() + '/v1/check_session', {
    credentials: 'include',
  });
  const gateway_session = await response.json();

  return gateway_session.is_valid;
}

//	------------------------------------------------------------
//
//	build the site name. we prefix the node if it is not
//	already prefixed to the site name.
//
//	------------------------------------------------------------

export function siteName(args: { site?: Site }) {
  //
  let name: string = '?';

  if (args.site !== undefined) {
    //
    name = args.site.site ?? '';
    const node = args.site.node?.toUpperCase() ?? '';
    const site = args.site.site?.toUpperCase() ?? '';

    if (!site.startsWith(node)) {
      name = node + ' ' + site;
    }
    //
  }

  return name;
  //
} // siteName

//	-------------------------------------------------
//
//	P R I V A T E   F U N C T I O N S
//
//	-------------------------------------------------

//	-------------------------------------------------
//
//	find the position of the nth occurrence of a
//	substring within a string.
//
//	-------------------------------------------------

const nthIndexOf = function (args: {
  inputString: string;
  findString: string;
  itemNumber: number;
}) {
  var i: number = -1;
  var n: number = args.itemNumber;
  while (n-- && i++ < args.inputString.length) {
    i = args.inputString.indexOf(args.findString, i);
    if (i < 0) break;
  }

  return i;
}; // nthIndexOf

//-------------------------------------------------
//
//  Helper function to switch class colors for each
//  status pill badge
//
//-------------------------------------------------
export function getStatusClass(args: { status: string }) {
  switch (args.status.toLowerCase()) {
    case 'running':
      return 'status-running';
    case 'stopped':
      return 'status-stopped';
    default:
      return 'status-unknown';
  }
}

//-------------------------------------------------
//
//  Helper function to replace newline delimiters
//  with HTML line breaks.
//
//-------------------------------------------------
export const addLineBreaks = (text: string | undefined) => {
  if (text === undefined) return;
  return text.split('\n').map((substr: string, i: number) => (
    <>
      {substr}
      <br />
    </>
  ));
};

//-------------------------------------------------
//
// function that handles a change in the desired page size
//
//-------------------------------------------------

export function pageSizeOnChangeHandler(
  event: React.ChangeEvent<HTMLSelectElement>,
  sPageSize: number,
  sPage: number[],
  sTotal: number[],
  setPage: (value: number[]) => void,
  setPages: (value: number[]) => void,
  setPageSize: (value: number) => void,
) {
  let newPageSize: number = sPageSize;
  try {
    newPageSize = parseInt(event.target.value);
  } catch {}

  const firstRow: number[] = sPage.map((p) => p * sPageSize);

  const currentPage: number[] = firstRow.map((row) => Math.floor(row / newPageSize));
  const pages: number[] = sTotal.map((total) => Math.ceil(total / newPageSize));

  setPage(currentPage);
  setPages(pages);
  setPageSize(newPageSize);
}

//	-------------------------------------------------
//
//	button handler for onClick events.
//
//	-------------------------------------------------

export function onClickHandler(
  event: React.MouseEvent<HTMLButtonElement>,
  tabID: string,
  sTab: number,
  sPage: number[],
  sPages: number[],
  sTabDropdownDisplayed: boolean,
  setPage: (value: number[]) => void,
  setTabDropdownDisplayed: (value: boolean) => void,
  setTab: (value: number) => void,
) {
  event.preventDefault();

  const button: HTMLButtonElement = event.currentTarget;

  //console.log('button.name:', button.name);

  // move to first page.
  if (button.name === 'searchResultsFirst' && sTab >= 0) {
    var currentPage: number[] = Object.assign([], sPage);
    currentPage[sTab] = 0;
    setPage(currentPage);
  }

  // move to previous page.
  if (button.name === 'searchResultsPrevious' && sTab >= 0) {
    var currentPage: number[] = Object.assign([], sPage);
    currentPage[sTab] = currentPage[sTab] - 1;
    setPage(currentPage);
  }

  // move to page before previous page.
  if (button.name === 'searchResultsMinus2' && sTab >= 0) {
    var currentPage: number[] = Object.assign([], sPage);
    currentPage[sTab] = currentPage[sTab] - 2;
    setPage(currentPage);
  }

  // move to previous page.
  if (button.name === 'searchResultsMinus1' && sTab >= 0) {
    var currentPage: number[] = Object.assign([], sPage);
    currentPage[sTab] = currentPage[sTab] - 1;
    setPage(currentPage);
  }

  // move to next page.
  if (button.name === 'searchResultsPlus1' && sTab >= 0) {
    var currentPage: number[] = Object.assign([], sPage);
    currentPage[sTab] = currentPage[sTab] + 1;
    setPage(currentPage);
  }

  // move to page after next page.
  if (button.name === 'searchResultsPlus2' && sTab >= 0) {
    var currentPage: number[] = Object.assign([], sPage);
    currentPage[sTab] = currentPage[sTab] + 2;
    setPage(currentPage);
  }

  // move to next page.
  if (button.name === 'searchResultsNext' && sTab >= 0) {
    var currentPage: number[] = Object.assign([], sPage);
    currentPage[sTab] = currentPage[sTab] + 1;
    setPage(currentPage);
  }

  // move to last page.
  if (button.name === 'searchResultsLast' && sTab >= 0) {
    var currentPage: number[] = Object.assign([], sPage);
    currentPage[sTab] = sPages[sTab] > 0 ? sPages[sTab] - 1 : 0;
    setPage(currentPage);
  }

  if (button.name === 'btnTabDropdown') setTabDropdownDisplayed(!sTabDropdownDisplayed);

  // if one of the tabs on a search results form has been pressed, then store the button name.
  const namePrefix = 'btnDataProduct_' + tabID + '_dpt';
  if (button.name.length > namePrefix.length)
    if (button.name.slice(0, namePrefix.length) === namePrefix) {
      // cut the rest of the text from the button name to get the data product type.
      let resultsTab: string = button.name.slice(namePrefix.length - button.name.length);

      // convert to an integer.
      var tabIndex: number = -1;
      try {
        tabIndex = parseInt(resultsTab);
      } catch {}

      // update the state.
      if (tabIndex >= 0) setTab(tabIndex);

      // ensure the tab dropdown is closed.
      setTabDropdownDisplayed(false);
    }
} // onClickHandler

//	------------------------------------------------------------
//
//	An asynchronous function that loads a list of data-management
//	API jobs.
//
//	------------------------------------------------------------
export function useDataManagementJobs() {
  const [jobs, setJobs] = useState<JobType[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const fetchJobs = useCallback(async () => {
    setLoading(true);

    try {
      const urlCommand = APIPrefix() + '/v1/data_management/list_jobs';

      const apiResult = await fetch(urlCommand, {
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      if (apiResult.status === 200) {
        const returnedJson = await apiResult.json();

        let jobsList: JobType[] = returnedJson.jobs_list ?? [];

        // remove "NOT FOUND"
        jobsList = jobsList.filter((j) => j.status !== 'NOT FOUND');

        setJobs(jobsList);
      } else if (apiResult.status === 401) {
        console.log('Session expired (401).');
        setJobs([]);
      } else {
        setJobs([]);
      }
    } catch (err) {
      console.error(err);
      setJobs([]);
    } finally {
      setLoading(false);
    }
  }, []);

  return { jobs, loading, fetchJobs, setJobs };
}

//	------------------------------------------------------------
//
//	Function to update current project in backend session
//
//	------------------------------------------------------------

export async function updateSession(args: { projectId?: number; siteId?: number }) {
  //
  let params: string = '';
  if (args.projectId !== undefined) {
    params = params + `project_id=${args.projectId}&`;
  }
  if (args.siteId !== undefined) {
    params = params + `site_id=${args.siteId}&`;
  }
  if (params.slice(-1) === '&') {
    params = params.slice(0, -1);
  }

  // only proceed if there is at least one parameter to update.
  if (params !== '') {
    try {
      const apiResult = await fetch(`${APIPrefix()}/v1/update_session?${params}`, {
        method: 'PUT',
        headers: {
          Accept: 'application/json',
        },
        credentials: 'include',
      });

      if (!apiResult.ok) {
        console.warn('Failed to update current project in session:', apiResult.status);
      }
    } catch (error) {
      console.error('Error updating current project:', error);
    }
  }
  //
} // updateSession

//	-------------------------------------------------
//
//	Gets the user's language from the browser
//	settings. If the language is hyphenated, i.e. en-US,
//	then we will only use the bit from before the hyphen.
//
//	-------------------------------------------------

export function getBrowserDefaultLanguage(args: { defaultLang: string }) {
  // get the browser's language.
  var browserLanguage = navigator.language;

  var language: string = args.defaultLang;
  if (browserLanguage !== '') {
    // extract the characters before the first '-'.
    const hyphenPos = browserLanguage.indexOf('-');
    if (hyphenPos > -1) language = browserLanguage.slice(0, hyphenPos);
    else language = browserLanguage;
  }

  // return something.
  return language;
} // getBrowserDefaultLanguage

//	-------------------------------------------------
//
//	Finds the index of the user's language in the
//	language dropdown box.
//
//	-------------------------------------------------

export function findLanguageIndex(args: { language: string | null }) {
  const lang = args.language;

  if (!lang) return 0;

  const index = LANGUAGE_OPTIONS.findIndex((element) => element.value === lang);

  return index === -1 ? 0 : index;
} // findLanguageIndex

//	------------------------------------------------------------
//
//	Get the current time in ISO format.
//
//	------------------------------------------------------------

export function getISOTime() {
  //
  const currentTime = new Date(Date.now());
  let isoTime: string = currentTime.toISOString();
  if (isoTime.length > 19) {
    isoTime = isoTime.slice(0, 19);
  }

  // return something.
  return isoTime;
  //
} // getISOTime
